/**
 * Generic upload button driving the 3-step presigned-PUT flow:
 *   1. POST to a prepare endpoint → receive { upload_url, artefact_key, required_headers }
 *   2. PUT the file directly to R2 at upload_url
 *   3. POST to a confirm endpoint with artefact_key → receive the resource update
 *
 * Owned by: storage-dev. Imported by per-Module pages (e.g. Question Bank's
 * AdminQuestionEditPage for image uploads).
 */

import { useRef, useState, type ReactElement } from 'react';

export interface UploadButtonProps {
  /** Accept attribute on the input — e.g. "image/png,image/jpeg,image/webp". */
  accept: string;
  /** Max size in bytes, enforced client-side for UX (server is the authority). */
  maxBytes: number;
  /**
   * Called with the chosen File. The caller orchestrates: prepareUpload,
   * direct PUT to R2, confirm. Returns success/error so the button can
   * surface a message.
   */
  onUpload: (file: File) => Promise<{ ok: boolean; message?: string }>;
  /** Optional label override. */
  label?: string;
}

export function UploadButton(props: UploadButtonProps): ReactElement {
  const { accept, maxBytes, onUpload, label } = props;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const handleClick = (): void => {
    if (inputRef.current) inputRef.current.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setInfo(null);
    if (file.size > maxBytes) {
      setError(
        `File ${file.name} is ${String(Math.round(file.size / 1024))} KB; max is ${String(Math.round(maxBytes / 1024))} KB.`,
      );
      return;
    }
    setBusy(true);
    void (async () => {
      try {
        const result = await onUpload(file);
        if (result.ok) {
          setInfo(result.message ?? 'Uploaded.');
        } else {
          setError(result.message ?? 'Upload failed.');
        }
      } finally {
        setBusy(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    })();
  };

  return (
    <div className="storage-upload-button">
      <input
        type="file"
        ref={inputRef}
        accept={accept}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <button type="button" onClick={handleClick} disabled={busy}>
        {busy ? 'Uploading…' : (label ?? 'Upload file')}
      </button>
      {error ? <p className="storage-upload-error">{error}</p> : null}
      {info ? <p className="storage-upload-info">{info}</p> : null}
    </div>
  );
}
